import { type StudyStepSlug, type StudyStepView, nextStudyStep } from '@sigora/domain';

/**
 * The steps the shell moves on from by itself, once a study read shows the step after them reached
 * while the researcher is watching: Launch once the study is launched, Fielding once fieldwork is
 * over and the answers are being read, and Analyzing once the Brief is written.
 */
export const AUTO_ADVANCE_STEPS: ReadonlySet<StudyStepSlug> = new Set([
  'launch',
  'fielding',
  'analyzing',
]);

/** Whether the study has reached a step: it is one of the study's steps and not `locked`. */
export const isReached = (steps: readonly StudyStepView[], slug: StudyStepSlug | null): boolean => {
  const status = steps.find((candidate) => candidate.slug === slug)?.status;

  return status !== undefined && status !== 'locked';
};

/**
 * Where a due move from `from` lands, or null when the step after it is not reached.
 *
 * The step after `from`, and on past every step the same read has already taken the study beyond:
 * while the step landed on is itself one the shell moves on from and the step after it is reached
 * too, the move goes on to that one. A study that went from fielding to complete between two reads
 * lands on its Brief rather than on an Analyzing step that is already done, which it would never
 * move on from, since arriving on a step whose next one is open never does. One destination, so
 * the move is one history entry and Back returns to the step that was being watched.
 */
export const autoAdvanceTarget = (
  steps: readonly StudyStepView[],
  from: StudyStepSlug,
): StudyStepSlug | null => {
  let target = nextStudyStep(from);
  if (target === null || !isReached(steps, target)) {
    return null;
  }

  for (
    let after = nextStudyStep(target);
    AUTO_ADVANCE_STEPS.has(target) && after !== null && isReached(steps, after);
    after = nextStudyStep(target)
  ) {
    target = after;
  }

  return target;
};
